import Vue from 'vue';
import Vuex from 'vuex';
import manageRoutes from '@/router/manage';
import { getSidebarMenu, findArrayDuplicate } from '@/libs/utils';
import { getCookie, removeToken } from '@/libs/cookie';
import config from '@/config';

import zhCN from 'ant-design-vue/lib/locale-provider/zh_CN';
import enUS from 'ant-design-vue/lib/locale-provider/en_US';

// 多语言数值匹配（跟后台）
const uiLocales = {
  'zh-CN': zhCN,
  'en-US': enUS,
};

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    // UI组件语言库
    uiCurrentLocale: uiLocales[getCookie('locale') || config.defaultLocale],

    // 当前语言Key值:'zh-CN','en-US'
    currentLocale: getCookie('locale') || config.defaultLocale,

    // 全局loading
    loading: true,

    // 页面loading
    pageLoading: true,

    // 冻结Loading
    freeze: false,

    // 面包屑导航
    breadcrumb: [],

    // 提示loading
    submitLoading: false,
    submitLoadingText: '',

    // 当前用户
    userInfo: null,
    // 菜单
    menu: null,
    menuList: [],
    // API权限
    authList: [],
    // token
    token: '',

    // 左侧导航(客户)
    sidebarMenuForCustomer: [],

    // 左侧导致
    sidebarMenuForAdmin: [],

    // 记录当前导航需要打开的父级菜单
    siderOpenKeys: [],

    // 系统当前时间
    systemTime: null,

    // 轮询间隔时间，默认30秒
    pollingTime: 30 * 1000,

    // 页面轮询对象
    pollingTimer: null,

    // 是否超级管理员
    isSuperAdmin: false,

    // 是否为infypower的客户（用户）
    isInfypowerCustomer: false,

    // 已经退出登录
    logout: false,

    // 消息轮询时间(30秒)
    messagePollingTime: 15 * 1000,
    messageUnreadNum: 0,
    isStayMessagePage: false,

    // 访问历史记录
    historyRouteList: [],
    historyRouteListNames: [],

    // oss信息
    ossConfig: null,
    ossClient: null,
    // 记录上次获取ossClient的时间
    ossClientStartTime: null,
    // ossClient刷新时间，这里前端半小时请求一次后台
    ossClientFreshInterval: 10 * 1000, // 30分钟
  },
  mutations: {
    // 清除登录态
    clearSession(state) {
      removeToken();
      state.token = '';
      state.userInfo = null;
      state.authList = [];
      state.menu = null;
      state.menuList = [];

      // 清除historyRouteList
      window.sessionStorage.removeItem('historyRouteList');
    },

    // 设置当前组件语言
    setUiCurrentLocale(state, data) {
      // 如果data有问题，则默认1
      state.uiCurrentLocale = uiLocales[data] || uiLocales[1];
      state.currentLocale = data || config.defaultLocale;
    },

    setBreadcrumb(state, data) {
      const list = [];

      if (data && data.matched) {
        data.matched.forEach(element => {
          if (element.meta && element.meta.breadcrumb !== false) {
            list.push(element);
          }
        });
      }
      // 是否有首页权限
      if (state.token) {
        if (!state.authList.includes('auth_home') || !state.isSuperAdmin) {
          list[0].path = '/manage/welcome';
          list[0].redirect = '/manage/welcome';
        }
      }
      state.breadcrumb = list;
    },

    /**
     * 过滤左则导航
     * @param {*} state state
     * @param {*} url 当前路由路径
     */
    setSidebar(state, url) {
      // 此处必须要深拷贝
      const routes = JSON.parse(JSON.stringify(manageRoutes));

      // 对客户的菜单
      // meta.sidebar===1
      const authRoutesForCustomer = getSidebarMenu(url, routes, state.userInfo, state.authList, 1);
      state.sidebarMenuForCustomer = authRoutesForCustomer;

      // 对管理员的菜单
      // meta.sidebar===2
      const authRoutesForAdmin = getSidebarMenu(url, routes, state.userInfo, state.authList, 2);
      state.sidebarMenuForAdmin = authRoutesForAdmin;
    },

    // 设置全屏Loading
    setLoading(state, status) {
      state.loading = status;
    },

    setPageLoading(state, status) {
      state.pageLoading = status;
    },

    setFreeze(state, status) {
      state.freeze = status;
    },

    setSubmitLoading(state, data) {
      if (typeof data === 'boolean') {
        state.submitLoading = data;
        state.submitLoadingText = null;
      } else {
        state.submitLoading = data.status;
        state.submitLoadingText = data.text;
      }
    },
    // 设置用户信息
    setUserInfo(state, data) {
      state.userInfo = data;

      // 设置权限
      state.authList = [];
      if (data && data.role && data.role.auth && Array.isArray(data.role.auth)) {
        state.authList = data.role.auth;
      }

      // 菜单
      state.menuList = [];
      if (data && data.role && data.role.menu) {
        state.menu = data.role.menu;
        state.menuList = data.role.menu;
      }

      // authList 合并menuList
      state.authList = state.authList.concat(state.menuList);

      // 检测权限key是否有重复，有重复则需要解决
      const duplicates = findArrayDuplicate(state.authList);
      if (duplicates && duplicates.length > 0) {
        // 权限authName存在在重复项
        // console.log(`Auth list has duplicates: ${duplicates}`);
      }

      // console.log('state.authList all', state.authList);
    },

    // 设置token
    setToken(state, token) {
      state.token = token;
    },

    // 设置导航需要打开的父级菜单
    setSiderOpenKeys(state, data) {
      state.siderOpenKeys = data;
    },

    setSystemTime(state, time) {
      state.systemTime = time;
    },

    // 设置轮询时间,time:毫秒
    setPollingTime(state, time) {
      state.pollingTime = time;
    },

    // 设置轮询
    setPolling(state, data) {
      clearTimeout(state.pollingTimer);
      let fn, time;
      // 是否为对象
      if (typeof data === 'object') {
        fn = data.fn;
        time = data.time;
      } else {
        fn = data;
      }
      // 必须有用户信息才轮询
      if (state.userInfo) {
        state.pollingTimer = setTimeout(function() {
          if (typeof fn === 'function') {
            fn && fn();
          }
        }, time || state.pollingTime);
      }
    },

    // 停止轮询
    stopPollingTimer(state) {
      clearTimeout(state.pollingTimer);
      // 停止轮询时，时间恢复到30秒
      state.pollingTime = 30 * 1000;
    },

    // 设置是否超级管理员
    setIsSuperAdmin(state, status) {
      state.isSuperAdmin = status;
    },

    // 设置是否为infypower的客户（用户）
    setIsInfypowerCustomer(state, status) {
      state.isInfypowerCustomer = status;
    },

    // 收集历史记录，并清除重复项
    setHistoryRouteList(state, route) {
      // 查询Session Storage里是否有
      if (!state.historyRouteList.length) {
        const _cacheList = window.sessionStorage.getItem('historyRouteList');
        if (_cacheList) {
          try {
            const _cacheListJSON = JSON.parse(_cacheList);
            state.historyRouteList = _cacheListJSON;
          } catch (err) {
            console.log(err);
          }
        } else {
          // 找出Home页面
          const routes = JSON.parse(JSON.stringify(manageRoutes));
          const homeRoute = routes.find(item => item.name === 'Home');
          homeRoute.fullPath = '/manage/home';
          if (!route) {
            route = homeRoute;
          }
        }
      }

      // 添加，仅记录管理后台的操作页面
      if (route && route.name && route.fullPath && /^\/manage\//gi.test(route.fullPath)) {
        const routes = (state.historyRouteList || []).map(item => item.fullPath);
        if (!routes.includes(route.fullPath)) {
          state.historyRouteList.push({
            name: route.name,
            fullPath: route.fullPath,
            meta: route.meta,
            title: route.title,
            index: state.historyRouteList.length + 1,
            type: route.type,
          });
        }

        // 最多记录20个
        const maxLength = 20;
        while (state.historyRouteList.length > maxLength) {
          state.historyRouteList.splice(1, 1); // 从第2个元素开始删除
        }

        // Home页面总是排在最前面
        state.historyRouteList = state.historyRouteList.sort((a, b) => {
          if (a.name === 'Home') return -1;
          if (b.name === 'Home') return 1;
          return 0;
        });

        // 同时存储一份Names
        state.historyRouteListNames = state.historyRouteList.map(item => item.name);

        // 同时存储到Session Storage
        window.sessionStorage.setItem('historyRouteList', JSON.stringify(state.historyRouteList));
      }
    },

    // 删除某个记录
    removeHistoryRouteList(state, route) {
      state.historyRouteList = state.historyRouteList.filter(
        item => item.fullPath !== route.fullPath
      );

      // 从Names中删除
      state.historyRouteListNames = state.historyRouteList.filter(item => item !== route.name);

      // 同时存储到Session Storage
      window.sessionStorage.setItem('historyRouteList', JSON.stringify(state.historyRouteList));
    },

    // 暂存消息的未读数
    setMessageUnreadNum(state, num) {
      state.messageUnreadNum = num;
    },

    // 设置是否停留在消息页面
    setIsStayMessagePage(state, status) {
      state.isStayMessagePage = status;
    },

    // 设置OSS
    setOssConfig(state, data) {
      state.ossConfig = data;
    },

    // 设置OSS客户端
    setOssClient(state, data) {
      state.ossClient = data;
    },

    // 设置OSS客户端刷新时间
    setOssClientStartTime(state, data) {
      state.ossClientStartTime = data;
    },
  },
  getters: {
    currentLocale: state => state.currentLocale,
    uiCurrentLocale: state => state.uiCurrentLocale,
    breadcrumb: state => state.breadcrumb,
    loading: state => state.loading,
    pageLoading: state => state.pageLoading,
    freeze: state => state.freeze,
    submitLoading: state => state.submitLoading,
    submitLoadingText: state => state.submitLoadingText,
    userInfo: state => state.userInfo,
    sidebarMenuForCustomer: state => state.sidebarMenuForCustomer,
    sidebarMenuForAdmin: state => state.sidebarMenuForAdmin,
    siderOpenKeys: state => state.siderOpenKeys,
    systemTime: state => state.systemTime,
    token: state => state.token,
    pollingTime: state => state.pollingTime,
    authList: state => state.authList,
    isSuperAdmin: state => state.isSuperAdmin,
    logout: state => state.logout,
    historyRouteList: state => state.historyRouteList,
  },
  actions: {},
  modules: {},
});
